import { ArrowDown, Mail, Phone, MessageCircle, Users, Clock, ShieldCheck, FileText } from "lucide-react";
import { LABSYSO_FEEDBACK } from "../../config/labsysoFeedback";

const StepCard = ({ step, title, children, variant = "default" }) => {
  const styles = {
    default: "bg-white border-[#221d52]/10 text-gray-700",
    accent: "bg-[#221d52] text-white border-[#221d52]",
    gold: "bg-[#f5b419]/15 border-[#f5b419]/40 text-[#221d52]",
    muted: "bg-gray-50 border-gray-200 text-gray-600",
  };

  return (
    <div className={`rounded-2xl border p-5 shadow-sm ${styles[variant]}`}>
      <div className="flex items-start gap-3">
        <span
          className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-xs font-black ${
            variant === "accent"
              ? "bg-[#f5b419] text-[#221d52]"
              : "bg-[#221d52]/10 text-[#221d52]"
          }`}
        >
          {step}
        </span>
        <div className="min-w-0">
          <h4 className="font-display font-bold text-sm md:text-base mb-2">{title}</h4>
          <div className="text-sm leading-relaxed space-y-2">{children}</div>
        </div>
      </div>
    </div>
  );
};

const ChannelGrid = ({ channels }) => (
  <div className="grid sm:grid-cols-2 gap-3 mt-3">
    {channels.map(({ label, href, icon: Icon, code, onClick }) => {
      const className =
        "flex items-center gap-3 p-3 rounded-xl bg-white border border-[#221d52]/10 hover:border-[#f5b419] hover:shadow-md transition-all group w-full text-left";

      const content = (
        <>
          <span className="w-10 h-10 rounded-xl bg-[#221d52]/5 text-[#221d52] flex items-center justify-center group-hover:bg-[#221d52] group-hover:text-[#f5b419] transition-colors">
            <Icon size={18} />
          </span>
          <span>
            <span className="block text-[10px] font-bold uppercase tracking-widest text-[#221d52]/50">
              {code}
            </span>
            <span className="block text-sm font-semibold text-[#221d52]">{label}</span>
          </span>
        </>
      );

      if (onClick) {
        return (
          <button key={code} type="button" onClick={onClick} className={className}>
            {content}
          </button>
        );
      }

      return (
        <a
          key={code}
          href={href}
          target={href?.startsWith("http") ? "_blank" : undefined}
          rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
          className={className}
        >
          {content}
        </a>
      );
    })}
  </div>
);

const LabsysoComplaintsFlowchart = ({ socialMedia = {}, onOpenForm }) => {
  const channels = [
    {
      code: "6.2.1",
      label: "Correo electrónico",
      href: socialMedia.email || "mailto:labsyso@agdysa.com",
      icon: Mail,
    },
    {
      code: "6.2.2",
      label: "Redes sociales (Facebook / WhatsApp)",
      href: socialMedia.whatsapp || socialMedia.facebook || "#",
      icon: MessageCircle,
    },
    {
      code: "6.2.3",
      label: "Llamadas telefónicas",
      href: socialMedia.telefono || "tel:50322747139",
      icon: Phone,
    },
    {
      code: "6.2.4",
      label: "Comunicación directa al personal",
      icon: Users,
      onClick: onOpenForm,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-[#221d52]/5 border border-[#221d52]/10 p-4 flex gap-3">
        <ShieldCheck className="text-[#221d52] shrink-0 mt-0.5" size={22} />
        <p className="text-sm text-[#221d52]/80 leading-relaxed">
          Procedimiento oficial de sugerencias, quejas y reclamos. Los plazos indicados son en{" "}
          <strong>días hábiles</strong>. Documento de referencia:{" "}
          <strong>{LABSYSO_FEEDBACK.formCode}</strong>.
        </p>
      </div>

      <StepCard step="6.1" title="Recepción de la solicitud">
        <p>
          Se recibe una sugerencia, queja o reclamo por cualquier medio. El cliente puede usar el
          enlace al buzón en línea y completar el formato{" "}
          <strong>{LABSYSO_FEEDBACK.formCode}</strong>.
        </p>
      </StepCard>

      <div className="flex justify-center text-[#221d52]/30">
        <ArrowDown size={20} />
      </div>

      <StepCard step="6.2" title="Canales de comunicación" variant="gold">
        <p>Medios habilitados para hacer llegar su sugerencia, queja o reclamo al laboratorio:</p>
        <ChannelGrid channels={channels} />
      </StepCard>

      <div className="flex justify-center text-[#221d52]/30">
        <ArrowDown size={20} />
      </div>

      <StepCard step="6.3" title="Registro y acuse de recibo">
        <p>
          El Director de Calidad registra cada solicitud en el formato <strong>F-PGC-6-2</strong>.
          Cuando aplica, se envía acuse de recibo al solicitante.
        </p>
      </StepCard>

      <div className="flex justify-center text-[#221d52]/30">
        <ArrowDown size={20} />
      </div>

      <StepCard step="6.4" title="Verificación y análisis" variant="accent">
        <p>
          El Director de Calidad, con el personal pertinente, verifica si la queja o sugerencia
          procede por estar relacionada con las actividades del laboratorio.
        </p>
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          <div className="rounded-xl bg-white/10 border border-white/15 p-4">
            <p className="font-bold text-[#f5b419] mb-1">No procede</p>
            <p className="text-white/85 text-sm">
              Se notifica a la persona correspondiente según aplique.
            </p>
          </div>
          <div className="rounded-xl bg-white/10 border border-white/15 p-4">
            <p className="font-bold text-[#f5b419] mb-1">Procede</p>
            <p className="text-white/85 text-sm">
              Se analiza, recopila información y se define solución inmediata. Registro en{" "}
              <strong>F-PGC-6-2</strong>.
            </p>
          </div>
        </div>
        <p className="flex items-start gap-2 mt-3 text-[#f5b419] text-xs font-semibold">
          <Clock size={14} className="shrink-0 mt-0.5" />
          Plazo máximo de análisis: {LABSYSO_FEEDBACK.analysisDeadlineDays} días hábiles desde la
          recepción. Si requiere más tiempo, se informa al cliente el estado y el plazo estimado.
        </p>
      </StepCard>

      <div className="flex justify-center text-[#221d52]/30">
        <ArrowDown size={20} />
      </div>

      <StepCard step="6.5" title="Comunicación al reclamante">
        <p>
          Siempre que sea posible, el laboratorio acusa recibo de la queja y facilita al
          reclamante el resultado del tratamiento, e informes de progreso cuando corresponda.
        </p>
      </StepCard>

      <StepCard step="6.6" title="Imparcialidad" variant="muted">
        <p>
          La investigación no dará lugar a acciones discriminatorias. La resolución será revisada
          por personas no involucradas en el objeto de la queja.
        </p>
      </StepCard>

      <StepCard step="6.7" title="Seguimiento y cierre">
        <p>
          Si la acción inmediata no resuelve el caso, se activa el procedimiento de no conformidad
          y acciones correctivas <strong>PGC-7</strong>. El Director de Calidad evalúa la
          efectividad de las soluciones y registra el cierre en <strong>F-PGC-6-2</strong>.
        </p>
        <p className="flex items-start gap-2 mt-2 text-[#221d52]/70 text-xs font-semibold">
          <Clock size={14} className="shrink-0 mt-0.5" />
          Evaluación de efectividad: máximo {LABSYSO_FEEDBACK.evaluationDeadlineDays} días hábiles
          posteriores a la implementación de las acciones.
        </p>
      </StepCard>

      <div className="rounded-2xl bg-[#f5b419] text-[#221d52] p-5 text-center font-display font-black uppercase tracking-widest text-sm">
        Fin del procedimiento
      </div>

      <a
        href={LABSYSO_FEEDBACK.flowchartPdfUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 w-full py-4 rounded-2xl border-2 border-[#221d52]/15 text-[#221d52] font-bold text-sm hover:bg-[#221d52]/5 transition-colors"
      >
        <FileText size={18} />
        Descargar flujograma oficial (PDF)
      </a>
    </div>
  );
};

export default LabsysoComplaintsFlowchart;
